import React from 'react';
import { clsx } from 'clsx';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import {
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { Task, TaskStatus, TaskPriority, TaskType } from '../../../types/task.types';
import { Badge, Avatar } from '../../ui';

interface TaskCardProps {
  task: Task;
  onClick?: () => void;
  onComplete?: () => void;
  onReopen?: () => void;
  variant?: 'default' | 'compact' | 'detailed';
  showAssignee?: boolean;
  showRelations?: boolean;
  isDragging?: boolean;
  className?: string;
}

const typeIcons: Record<TaskType, React.ReactNode> = {
  CALL: <PhoneIcon className="h-4 w-4" />,
  EMAIL: <EnvelopeIcon className="h-4 w-4" />,
  MEETING: <UserGroupIcon className="h-4 w-4" />,
  TODO: <CheckCircleIcon className="h-4 w-4" />,
  FOLLOW_UP: <ClockIcon className="h-4 w-4" />,
  CUSTOM: <CheckCircleIcon className="h-4 w-4" />,
};

const typeColors: Record<TaskType, string> = {
  CALL: 'text-blue-600 bg-blue-50',
  EMAIL: 'text-purple-600 bg-purple-50',
  MEETING: 'text-green-600 bg-green-50',
  TODO: 'text-gray-600 bg-gray-50',
  FOLLOW_UP: 'text-orange-600 bg-orange-50',
  CUSTOM: 'text-indigo-600 bg-indigo-50',
};

const priorityStyles: Record<TaskPriority, { bg: string; text: string; border: string }> = {
  LOW: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
  NORMAL: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  MEDIUM: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  HIGH: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
  URGENT: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
};

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onClick,
  onComplete,
  onReopen,
  variant = 'default',
  showAssignee = true,
  showRelations = true,
  isDragging = false,
  className,
}) => {
  const isOverdue = task.dueDateTime && isPast(new Date(task.dueDateTime)) && task.status !== TaskStatus.COMPLETED;
  const isCompleted = task.status === TaskStatus.COMPLETED;
  const priorityStyle = priorityStyles[task.priority];

  const formatDueDate = (date: string) => {
    const d = new Date(date);
    if (isToday(d)) return `Today ${format(d, 'h:mm a')}`;
    if (isTomorrow(d)) return `Tomorrow ${format(d, 'h:mm a')}`;
    return format(d, 'MMM d, h:mm a');
  };

  const handleCompleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCompleted) {
      onReopen?.();
    } else {
      onComplete?.();
    }
  };

  return (
    <div
      onClick={onClick}
      className={clsx(
        'group relative bg-white rounded-xl border transition-all duration-200',
        'hover:shadow-lg hover:border-primary-200 hover:-translate-y-0.5',
        isDragging && 'shadow-2xl rotate-2 scale-105 opacity-90',
        isCompleted && 'opacity-60',
        priorityStyle.border,
        onClick && 'cursor-pointer',
        className
      )}
    >
      {/* Priority indicator bar */}
      <div className={clsx('absolute left-0 top-0 bottom-0 w-1 rounded-l-xl', priorityStyle.bg)} />

      <div className="p-4 pl-5">
        {/* Header row */}
        <div className="flex items-start gap-3">
          {/* Complete button */}
          <button
            onClick={handleCompleteClick}
            className={clsx(
              'flex-shrink-0 mt-0.5 transition-all duration-200',
              isCompleted
                ? 'text-green-500 hover:text-green-600'
                : 'text-gray-300 hover:text-green-500 hover:scale-110'
            )}
          >
            {isCompleted ? (
              <CheckCircleSolidIcon className="h-6 w-6" />
            ) : (
              <div className="h-6 w-6 rounded-full border-2 border-current flex items-center justify-center group-hover:border-green-400">
                <CheckCircleIcon className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {/* Type icon */}
              <span className={clsx('p-1 rounded-md', typeColors[task.type])}>
                {typeIcons[task.type]}
              </span>
              
              {/* Title */}
              <h3 className={clsx(
                'font-medium text-gray-900 truncate',
                isCompleted && 'line-through text-gray-500'
              )}>
                {task.title}
              </h3>
            </div>

            {/* Description */}
            {task.description && variant !== 'compact' && (
              <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                {task.description}
              </p>
            )}

            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {/* Due date */}
              {task.dueDateTime && (
                <span className={clsx(
                  'flex items-center gap-1 px-2 py-1 rounded-full',
                  isOverdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                )}>
                  {isOverdue && <ExclamationTriangleIcon className="h-3 w-3" />}
                  <ClockIcon className="h-3 w-3" />
                  {formatDueDate(task.dueDateTime)}
                </span>
              )}

              {/* Priority badge */}
              <Badge size="sm" variant={task.priority === 'URGENT' ? 'error' : task.priority === 'HIGH' ? 'warning' : 'neutral'}>
                {task.priority}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;

